import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";
import { WhatsAppIcon } from "@/components/site/icons";
import { waLink } from "@/lib/contact";

const NAV = [
  { id: "ayuda", label: "Cómo ayudamos" },
  { id: "modelo", label: "Modelo" },
  { id: "familia", label: "Familia" },
  { id: "confianza", label: "Confianza" },
  { id: "faq", label: "Preguntas" },
  { id: "contacto", label: "Contacto" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 10);
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-line bg-plaster/85 backdrop-blur-md backdrop-saturate-150" : "border-transparent bg-plaster/60 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5 md:px-8">
        <Logo />

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Principal">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              className="text-[0.95rem] font-medium text-ink/85 transition-colors hover:text-spring"
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={waLink("Hola, quiero información sobre CESMA.")}
            target="_blank"
            rel="noopener"
            className="hidden items-center gap-2 rounded-full bg-marigold px-5 py-3 text-[0.95rem] font-semibold text-[#231402] shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0994c] hover:shadow-lift sm:inline-flex"
            data-testid="header-whatsapp"
          >
            <WhatsAppIcon className="h-[1.15em] w-[1.15em]" />
            WhatsApp
          </a>
          <button
            className="grid h-11 w-11 place-items-center rounded-full text-pine lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            data-testid="menu-toggle"
          >
            {open ? <X strokeWidth={1.8} /> : <Menu strokeWidth={1.8} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-t border-line bg-warm lg:hidden"
            aria-label="Menú móvil"
          >
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  onClick={() => go(n.id)}
                  className="rounded-xl px-3 py-3 text-left text-base font-medium text-ink transition-colors hover:bg-band"
                  data-testid={`nav-mobile-${n.id}`}
                >
                  {n.label}
                </button>
              ))}
              <a
                href={waLink("Hola, quiero información sobre CESMA.")}
                target="_blank"
                rel="noopener"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-marigold px-5 py-3 font-semibold text-[#231402]"
                data-testid="menu-whatsapp"
              >
                <WhatsAppIcon className="h-[1.15em] w-[1.15em]" />
                WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
